import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CampaignCard = ({ campaign, onEdit, onPause, onViewDetails }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success';
      case 'paused':
        return 'bg-warning/10 text-warning';
      case 'completed':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return 'Play';
      case 'paused':
        return 'Pause';
      case 'completed':
        return 'CheckCircle';
      default:
        return 'Circle';
    }
  };

  const budgetPercentage = (campaign?.spent / campaign?.budget) * 100;

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border hover:shadow-lg transition-all duration-250">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg md:text-xl font-semibold text-foreground truncate">
              {campaign?.name}
            </h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0 ${getStatusColor(campaign?.status)}`}>
              <Icon name={getStatusIcon(campaign?.status)} size={12} />
              {campaign?.status?.charAt(0)?.toUpperCase() + campaign?.status?.slice(1)}
            </span>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {campaign?.description}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            onClick={() => onEdit(campaign?.id)}
          >
            Edit
          </Button>
          <Button
            variant={campaign?.status === 'active' ? 'warning' : 'success'}
            size="sm"
            iconName={campaign?.status === 'active' ? 'Pause' : 'Play'}
            onClick={() => onPause(campaign?.id)}
          >
            {campaign?.status === 'active' ? 'Pause' : 'Resume'}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
        <div className="bg-primary/5 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Users" size={16} className="text-primary" />
            <span className="text-xs text-muted-foreground">Participants</span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-foreground data-text">
            {campaign?.participants?.toLocaleString()}
          </p>
        </div>

        <div className="bg-secondary/5 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="TrendingUp" size={16} className="text-secondary" />
            <span className="text-xs text-muted-foreground">Engagement</span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-foreground data-text">
            {campaign?.engagementRate}%
          </p>
        </div>

        <div className="bg-success/5 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="DollarSign" size={16} className="text-success" />
            <span className="text-xs text-muted-foreground">Cashback</span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-foreground data-text">
            {campaign?.cashbackRate}%
          </p>
        </div>

        <div className="bg-accent/5 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Target" size={16} className="text-accent" />
            <span className="text-xs text-muted-foreground">Conversions</span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-foreground data-text">
            {campaign?.conversions?.toLocaleString()}
          </p>
        </div>
      </div>
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Budget Utilization</span>
          <span className="font-semibold text-foreground data-text">
            ${campaign?.spent?.toLocaleString()} / ${campaign?.budget?.toLocaleString()}
          </span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
            style={{ width: `${Math.min(budgetPercentage, 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{budgetPercentage?.toFixed(1)}% used</span>
          <span>${(campaign?.budget - campaign?.spent)?.toLocaleString()} remaining</span>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          size="sm"
          iconName="BarChart3"
          onClick={() => onViewDetails(campaign?.id)}
          fullWidth
        >
          View Analytics
        </Button>
        <Button
          variant="outline"
          size="sm"
          iconName="Copy"
          fullWidth
        >
          Duplicate Campaign
        </Button>
      </div>
    </div>
  );
};

export default CampaignCard;